import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { TeamCard, TeamCardMini } from '../components/TeamCard';
import { Standings } from '../components/Standings';
import { YouthAcademy } from '../components/YouthAcademy';
import { AwardRace } from '../components/AwardRace';
import { TacticalPitch } from '../components/TacticalPitch';
import { MatchPitchGraphics } from '../components/MatchPitchGraphics';
import { MatchEvents } from '../components/MatchEvents';
import { SubstitutionModal } from '../components/SubstitutionModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { createInitialGameState, getTeamOverall, generatePlayerForMarket, generateSeasonFixtures, FORMATIONS, saveGame, calculateStandings, EMPTY_STATS, EMPTY_COMP_STATS, getPlayerStat, COMPETITIONS, buildLineupForFormation, getMixedUpcomingForTeam } from '../utils/gameData';
import { simulateWeek, quickSimulate, fullSimulate, makeSubstitution, updatePlayerStats, applyMatchGrowth, updateTeamStats, simulateKnockoutMatch } from '../utils/simulationEngine';
import { calculatePlayerValue, formatValue, applySeasonGrowth, generateTransferOffer } from '../utils/transferSystem';
import { runAITransferWindow } from '../utils/aiTransfers';
import { buildCWCTeams } from '../utils/realTeams';
import { buildWorldCupTeams } from '../utils/worldCup';
import { generateChampionsLeagueBracket } from '../utils/championsLeague';
import { CupPage } from './CupPage';
import { AsianCupPage } from './AsianCupPage';
import { ClubWorldCupPage } from './ClubWorldCupPage';
import { WorldCupPage } from './WorldCupPage';
import { CompetitionAwards } from '../components/CompetitionAwards';
import {
  ArrowLeft, Crown, Play, Pause, FastForward, ShoppingCart, CurrencyDollar,
  ArrowUp, Lightning, Check, X, Strategy, Users as UsersIcon,
  ArrowsLeftRight, FloppyDisk, GraduationCap, Trophy, ChartBar, Eye,
  CalendarDots, Globe, SoccerBall, GameController, Plus, Lock, ArrowCounterClockwise
} from '@phosphor-icons/react';

const POS_CLR = (pos) => {
  if (pos === 'GK') return 'position-gk';
  if (['CB','LB','RB','LWB','RWB','SW'].includes(pos)) return 'position-def';
  if (['CDM','CM','CAM','LM','RM'].includes(pos)) return 'position-mid';
  return 'position-att';
};

// ============ CUP LOGIC (self-contained) ============
const genCupGroups = (teams) => {
  const s = [...teams].sort(() => Math.random() - 0.5);
  const groups = [];
  for (let i = 0; i < 4; i++) {
    groups.push({ name: String.fromCharCode(65 + i), teams: s.slice(i*5, i*5+5).map(t => ({ ...t, cupStats: { played:0, won:0, drawn:0, lost:0, gf:0, ga:0, points:0 } })), fixtures: [], completed: false });
  }
  groups.forEach(g => {
    for (let i = 0; i < g.teams.length; i++)
      for (let j = i+1; j < g.teams.length; j++)
        g.fixtures.push({ homeId: g.teams[i].id, awayId: g.teams[j].id, homeScore: null, awayScore: null, played: false });
  });
  return groups;
};
const simCupGroups = (groups, allTeams) => {
  let g = groups.map(gr => ({ ...gr, teams: [...gr.teams], fixtures: [...gr.fixtures] }));
  g.forEach((group, gi) => {
    group.fixtures.forEach((f, fi) => {
      if (f.played) return;
      const home = allTeams.find(t => t.id === f.homeId) || group.teams.find(t => t.id === f.homeId);
      const away = allTeams.find(t => t.id === f.awayId) || group.teams.find(t => t.id === f.awayId);
      if (!home || !away) return;
      const r = quickSimulate(home, away);
      g[gi].fixtures[fi] = { ...f, homeScore: r.homeScore, awayScore: r.awayScore, played: true };
      const hIdx = g[gi].teams.findIndex(t => t.id === home.id);
      const aIdx = g[gi].teams.findIndex(t => t.id === away.id);
      const isHW = r.homeScore > r.awayScore, isDraw = r.homeScore === r.awayScore;
      if (hIdx !== -1) { const s = { ...g[gi].teams[hIdx].cupStats }; s.played++; s.gf += r.homeScore; s.ga += r.awayScore; if (isHW) { s.won++; s.points+=3; } else if (isDraw) { s.drawn++; s.points+=1; } else s.lost++; g[gi].teams[hIdx] = { ...g[gi].teams[hIdx], cupStats: s }; }
      if (aIdx !== -1) { const s = { ...g[gi].teams[aIdx].cupStats }; s.played++; s.gf += r.awayScore; s.ga += r.homeScore; if (!isHW && !isDraw) { s.won++; s.points+=3; } else if (isDraw) { s.drawn++; s.points+=1; } else s.lost++; g[gi].teams[aIdx] = { ...g[gi].teams[aIdx], cupStats: s }; }
    });
    g[gi].completed = g[gi].fixtures.every(fx => fx.played);
  });
  return g;
};
const cupGroupStandings = (group) => [...group.teams].sort((a,b) => b.cupStats.points - a.cupStats.points || (b.cupStats.gf-b.cupStats.ga) - (a.cupStats.gf-a.cupStats.ga));

// ============ KNOCKOUT HELPER ============
const simKOround = (round, allTeams, isFinal = false) => {
  const results = round.map(tie => {
    if (tie.winner) return tie;
    const h = allTeams.find(t => t.id === tie.home.id) || tie.home;
    const a = allTeams.find(t => t.id === tie.away.id) || tie.away;
    const r = quickSimulate(h, a);
    if (isFinal) return { ...tie, homeScore: r.homeScore, awayScore: r.awayScore, played: true, winner: r.homeScore >= r.awayScore ? tie.home : tie.away };
    const leg2 = quickSimulate(a, h);
    const hAgg = r.homeScore + leg2.awayScore, aAgg = r.awayScore + leg2.homeScore;
    return { ...tie, leg1: { h: r.homeScore, a: r.awayScore }, leg2: { h: leg2.homeScore, a: leg2.awayScore }, played: true, winner: hAgg > aAgg ? tie.home : aAgg > hAgg ? tie.away : (Math.random() > 0.5 ? tie.home : tie.away) };
  });
  const winners = results.filter(t => t.winner).map(t => t.winner);
  const next = [];
  for (let i = 0; i < winners.length; i += 2) if (winners[i] && winners[i+1]) next.push({ home: winners[i], away: winners[i+1], played: false, winner: null });
  return { results, next };
};

export const ManagerPage = ({ gameState, onUpdateGameState, onResetManager }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(() => gameState?.managerMode?.teamId ? 'dashboard' : 'select');
  const [selectedTeamId, setSelectedTeamId] = useState(gameState?.managerMode?.teamId || null);
  const [activeTab, setActiveTab] = useState('standings');
  const [marketPlayers, setMarketPlayers] = useState(() => gameState?.managerMode?.market || Array.from({ length: 15 }, () => generatePlayerForMarket()));
  const [incomingOffers, setIncomingOffers] = useState(() => gameState?.managerMode?.offers || []);
  const [showSubModal, setShowSubModal] = useState(false);
  const [scoutTeamId, setScoutTeamId] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  // Fixture match sim
  const [fixMatch, setFixMatch] = useState(null);
  const [fixSimMode, setFixSimMode] = useState(null);
  const [fixEvents, setFixEvents] = useState([]);
  const [fixMinute, setFixMinute] = useState(0);
  const [fixState, setFixState] = useState('idle');
  const [fixPaused, setFixPaused] = useState(false);
  const fixControlRef = React.useRef(null);

  const mgr = gameState?.managerMode || {};
  const budget = mgr.budget || 50;
  const myTeam = gameState?.teams?.find(t => t.id === selectedTeamId);
  const seasonDone = gameState?.currentWeek > 38;

  // Cup state stored in managerMode
  const mgrCup = mgr.cup || null;
  const mgrChampionsLeague = mgr.championsLeague || null;
  const mgrCWC = mgr.cwc || null;
  const mgrWC = mgr.worldCup || null;

  // Check if all cups are done for season advancement
  const leagueDone = seasonDone;
  const cupDone = !mgrCup || mgrCup.phase === 'complete';
  const acDone = !mgrChampionsLeague || mgrChampionsLeague.complete;
  const cwcDone = !mgrCWC || mgrCWC.complete;
  const wcDone = !mgrWC || mgrWC.complete;
  const allCupsDone = leagueDone && cupDone && acDone && cwcDone && wcDone;

  const updateMgr = (updates) => onUpdateGameState({ ...gameState, managerMode: { ...gameState.managerMode, ...updates } });

  const startManager = (isNew = false) => {
    let gs = gameState;
    if (!gs?.teams?.length || isNew) gs = createInitialGameState();
    if (!selectedTeamId && !isNew) return;
    const tid = selectedTeamId || gs.teams[0]?.id;
    const mkt = Array.from({ length: 15 }, () => generatePlayerForMarket());
    setMarketPlayers(mkt);
    setIncomingOffers([]);
    const newGs = { ...gs, mode: 'manager', managerMode: { teamId: tid, budget: 50, season: 1, market: mkt, offers: [], cup: null, championsLeague: null, cwc: null, worldCup: null } };
    onUpdateGameState(newGs);
    setSelectedTeamId(tid);
    setPhase('dashboard');
  };

  const simWeek = () => {
    if (gameState.currentWeek > 38) return;
    const result = simulateWeek(gameState.teams, gameState.fixtures, gameState.currentWeek);
    onUpdateGameState({ ...gameState, teams: result.teams, fixtures: result.fixtures, currentWeek: gameState.currentWeek + 1 });
    if (Math.random() > 0.6 && myTeam) {
      const eligible = myTeam.players.filter(p => calculatePlayerValue(p) > 2);
      if (eligible.length > 0) {
        const target = eligible[Math.floor(Math.random() * eligible.length)];
        const offer = generateTransferOffer(target, myTeam.name);
        setIncomingOffers(prev => [...prev, offer]);
      }
    }
  };
  const simAllRemaining = () => {
    let gs = { ...gameState };
    for (let w = gs.currentWeek; w <= 38; w++) {
      const r = simulateWeek(gs.teams, gs.fixtures, w);
      gs = { ...gs, teams: r.teams, fixtures: r.fixtures, currentWeek: w + 1 };
    }
    onUpdateGameState(gs);
  };

  const buyPlayer = (player) => {
    const cost = calculatePlayerValue(player);
    if (cost > budget || !myTeam || myTeam.players.length >= 25) return;
    const num = Math.max(...myTeam.players.map(p => p.number)) + 1;
    const np = { ...player, number: num };
    const ut = { ...myTeam, players: [...myTeam.players, np], bench: [...myTeam.bench, num] };
    const uts = gameState.teams.map(t => t.id === selectedTeamId ? ut : t);
    setMarketPlayers(prev => prev.filter(p => p.id !== player.id));
    onUpdateGameState({ ...gameState, teams: uts, managerMode: { ...gameState.managerMode, budget: Math.round((budget - cost)*10)/10 } });
  };
  const sellPlayer = (player) => {
    if (!myTeam || myTeam.players.length <= 15) return;
    const v = calculatePlayerValue(player);
    const ut = { ...myTeam, players: myTeam.players.filter(p => p.id !== player.id), lineup: myTeam.lineup.filter(n => n !== player.number), bench: myTeam.bench.filter(n => n !== player.number) };
    if (ut.lineup.length < 11 && ut.bench.length > 0) ut.lineup.push(ut.bench.shift());
    onUpdateGameState({ ...gameState, teams: gameState.teams.map(t => t.id === selectedTeamId ? ut : t), managerMode: { ...gameState.managerMode, budget: Math.round((budget+v)*10)/10 } });
  };
  const acceptOffer = (offer) => { sellPlayer(offer.player); setIncomingOffers(prev => prev.map(o => o.id === offer.id ? { ...o, status: 'accepted' } : o)); };
  const rejectOffer = (offer) => { setIncomingOffers(prev => prev.map(o => o.id === offer.id ? { ...o, status: 'rejected' } : o)); };

  const advanceSeason = () => {
    if (!allCupsDone) return;
    // Run AI transfer window across all teams except user's team
    const { teams: transferredTeams, log: aiTransferLog } = runAITransferWindow(gameState.teams, { excludeTeamId: gameState.managerMode?.teamId });
    const grownTeams = transferredTeams.map(team => ({
      ...team, players: team.players.map(p => applySeasonGrowth(p)),
      stats: { played:0, won:0, drawn:0, lost:0, goalsFor:0, goalsAgainst:0, points:0 }
    }));
    const newFixtures = generateSeasonFixtures(grownTeams);
    const newMarket = Array.from({ length: 15 }, () => generatePlayerForMarket());
    // Regenerate WC/CWC participants so different nations & teams show up every cycle
    const newWcTeams = buildWorldCupTeams(grownTeams);
    const newCwcTeams = buildCWCTeams();
    setMarketPlayers(newMarket); setIncomingOffers([]);
    onUpdateGameState({ ...gameState, teams: grownTeams, fixtures: newFixtures, currentWeek: 1, season: (gameState.season||1)+1,
      wcTeams: newWcTeams, cwcTeams: newCwcTeams, worldCup: null, cwc: null,
      aiTransferLog,
      managerMode: { ...gameState.managerMode, season: (mgr.season||1)+1, budget: budget+20, market: newMarket, offers: [], cup: null, championsLeague: null, cwc: null, worldCup: null } });
  };

  const handleQuickSave = () => { saveGame(gameState, 'manager_quicksave'); setSaveMsg('Saved!'); setTimeout(() => setSaveMsg(null), 2000); };

  // --- CUP ---
  const startCup = () => { const g = genCupGroups(gameState.teams); updateMgr({ cup: { groups: g, ko: null, phase: 'group' } }); };
  const simCupGroupsAll = () => {
    if (!mgrCup) return;
    const g = simCupGroups(mgrCup.groups, gameState.teams);
    updateMgr({ cup: { ...mgrCup, groups: g, phase: g.every(gr => gr.completed) ? 'group_done' : 'group' } });
  };
  const cupAdvanceKO = () => {
    const qualifiers = [];
    mgrCup.groups.forEach(g => { const s = cupGroupStandings(g); qualifiers.push(s[0], s[1]); });
    const sh = qualifiers.sort(() => Math.random() - 0.5);
    const qf = [];
    for (let i = 0; i < 4; i++) qf.push({ home: sh[i*2], away: sh[i*2+1], played: false, winner: null });
    updateMgr({ cup: { ...mgrCup, ko: { qf, sf: [], final: [] }, phase: 'qf' } });
  };
  const cupSimKO = (roundKey) => {
    const round = mgrCup.ko[roundKey];
    const isFinal = roundKey === 'final';
    const { results, next } = simKOround(round, gameState.teams, isFinal);
    const newKO = { ...mgrCup.ko, [roundKey]: results };
    let nextPhase = mgrCup.phase;
    if (roundKey === 'qf' && next.length) { newKO.sf = next; nextPhase = 'sf'; }
    else if (roundKey === 'sf' && next.length) { newKO.final = next; nextPhase = 'final'; }
    else if (roundKey === 'final') nextPhase = 'complete';
    updateMgr({ cup: { ...mgrCup, ko: newKO, phase: nextPhase } });
  };

  // --- ICL (Inter-Continental League) ---
  const canStartAC = seasonDone;
  const top4 = calculateStandings(gameState.teams || []).slice(0, 4);
  const startAC = () => { const b = generateChampionsLeagueBracket(top4); updateMgr({ championsLeague: b }); };
  const simACround = (roundKey) => {
    const round = mgrChampionsLeague[roundKey];
    const results = round.map(tie => {
      if (tie.played) return tie;
      const r = quickSimulate(tie.home, tie.away);
      return { ...tie, homeScore: r.homeScore, awayScore: r.awayScore, played: true, winner: r.homeScore >= r.awayScore ? tie.home : tie.away };
    });
    const winners = results.filter(t => t.winner).map(t => t.winner);
    const roundOrder = ['round32','round16','qf','sf','final'];
    const ci = roundOrder.indexOf(roundKey);
    const nextKey = roundOrder[ci+1];
    const upd = { ...mgrChampionsLeague, [roundKey]: results };
    if (nextKey && winners.length >= 2) {
      const nm = [];
      for (let i = 0; i < winners.length; i+=2) if (winners[i] && winners[i+1]) nm.push({ home: winners[i], away: winners[i+1], homeScore: null, awayScore: null, played: false, winner: null });
      upd[nextKey] = nm; upd.phase = nextKey;
    }
    if (roundKey === 'final' && results[0]?.winner) { upd.complete = true; upd.phase = 'complete'; }
    updateMgr({ championsLeague: upd });
  };

  // --- FIXTURE MATCH ---
  const startFixMatch = (fixture) => {
    const h = gameState.teams.find(t => t.id === fixture.homeTeamId);
    const a = gameState.teams.find(t => t.id === fixture.awayTeamId);
    if (!h || !a || fixture.played) return;
    setFixMatch({ fixture, home: h, away: a });
    setFixEvents([]); setFixMinute(0); setFixState('idle'); setFixSimMode(null);
  };
  const runFixQuick = () => {
    if (!fixMatch) return;
    setFixSimMode('quick'); setFixState('live');
    const r = quickSimulate(fixMatch.home, fixMatch.away);
    setFixEvents(r.events); setFixMinute(90); setFixState('done');
    // Apply to league
    const fIdx = gameState.fixtures.findIndex(f => f.id === fixMatch.fixture.id);
    if (fIdx !== -1) {
      const uf = [...gameState.fixtures]; uf[fIdx] = { ...uf[fIdx], homeScore: r.homeScore, awayScore: r.awayScore, played: true, events: r.events, matchRatings: r.matchRatings };
      let ut = [...gameState.teams];
      const hi = ut.findIndex(t => t.id === fixMatch.home.id);
      const ai = ut.findIndex(t => t.id === fixMatch.away.id);
      const hw = r.homeScore > r.awayScore, dr = r.homeScore === r.awayScore;
      ut[hi] = updateTeamStats(ut[hi], r.homeScore, r.awayScore, hw, dr);
      ut[ai] = updateTeamStats(ut[ai], r.awayScore, r.homeScore, !hw && !dr, dr);
      ut = updatePlayerStats(ut, r.events, fixMatch.home.name, fixMatch.away.name, r.homeScore, r.awayScore, 'league');
      ut = applyMatchGrowth(ut, r.matchRatings);
      onUpdateGameState({ ...gameState, teams: ut, fixtures: uf });
    }
  };
  const runFixFull = () => {
    if (!fixMatch) return;
    setFixSimMode('full'); setFixState('live'); setFixEvents([]); setFixMinute(0); setFixPaused(false);
    const control = fullSimulate(fixMatch.home, fixMatch.away,
      (e) => { if (e.type === 'minute') setFixMinute(e.minute); else setFixEvents(prev => [...prev, e]); },
      (r) => { setFixMinute(90); setFixState('done');
        const fIdx = gameState.fixtures.findIndex(f => f.id === fixMatch.fixture.id);
        if (fIdx !== -1) {
          const uf = [...gameState.fixtures]; uf[fIdx] = { ...uf[fIdx], homeScore: r.homeScore, awayScore: r.awayScore, played: true, events: r.events };
          let ut = [...gameState.teams];
          const hi = ut.findIndex(t => t.id === fixMatch.home.id); const ai = ut.findIndex(t => t.id === fixMatch.away.id);
          const hw = r.homeScore > r.awayScore, dr = r.homeScore === r.awayScore;
          ut[hi] = updateTeamStats(ut[hi], r.homeScore, r.awayScore, hw, dr);
          ut[ai] = updateTeamStats(ut[ai], r.awayScore, r.homeScore, !hw && !dr, dr);
          ut = updatePlayerStats(ut, r.events, fixMatch.home.name, fixMatch.away.name, r.homeScore, r.awayScore, 'league');
          onUpdateGameState({ ...gameState, teams: ut, fixtures: uf });
        }
      }, 80);
    fixControlRef.current = control;
  };
  const toggleFixPause = () => {
    if (!fixControlRef.current) return;
    if (fixPaused) { fixControlRef.current.resume(); setFixPaused(false); }
    else { fixControlRef.current.pause(); setFixPaused(true); }
  };

  const fixHomeScore = fixEvents.filter(e => e.type === 'goal' && e.team === 'home').length;
  const fixAwayScore = fixEvents.filter(e => e.type === 'goal' && e.team === 'away').length;

  // ============ TEAM SELECTION ============
  if (phase === 'select') {
    return (
      <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white"><ArrowLeft size={20} /></Button>
            <div><h1 className="font-heading font-black text-3xl text-white uppercase">Road to Glory</h1><p className="text-[#A0A0A5] mt-1">Pick your team</p></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {(gameState?.teams?.length ? gameState.teams : []).map(team => (
              <TeamCard key={team.id} team={team} onClick={() => setSelectedTeamId(team.id)} isSelected={selectedTeamId === team.id} />
            ))}
          </div>
          <div className="mt-6 flex gap-4 justify-center">
            <Button data-testid="start-manager-btn" onClick={() => startManager(false)} disabled={!selectedTeamId} className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black font-bold px-8 py-6 text-lg">
              <Crown size={24} className="mr-2" weight="fill" /> Start Career
            </Button>
            <Button data-testid="new-manager-save-btn" onClick={() => startManager(true)} className="bg-transparent border-2 border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-black font-bold px-6 py-6 text-lg">
              <Plus size={24} className="mr-2" /> New Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============ DASHBOARD ============
  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white"><ArrowLeft size={20} /></Button>
            <div>
              <h1 className="font-heading font-black text-xl sm:text-2xl text-white uppercase">{myTeam?.name || 'Manager'}</h1>
              <p className="text-[#A0A0A5] text-xs">S{gameState.season||1} W{gameState.currentWeek}/38 | <span className="text-[#32D74B] font-mono">{formatValue(budget)}</span>
                {!allCupsDone && seasonDone && <span className="text-[#FF6B00] ml-2">Finish all cups before next season</span>}
              </p>
            </div>
          </div>
          <div className="flex gap-1.5 items-center flex-wrap justify-end">
            {saveMsg && <span className="text-xs text-[#32D74B]">{saveMsg}</span>}
            <Button onClick={handleQuickSave} variant="outline" className="bg-transparent border-[#32D74B] text-[#32D74B] hover:bg-[#32D74B] hover:text-black h-8 text-xs px-2" data-testid="mgr-quick-save"><FloppyDisk size={14} className="mr-1" />Save</Button>
            {onResetManager && (
              <Button onClick={() => { if (window.confirm('Start a NEW Road to Glory save? Your current career will be erased.')) onResetManager(); }}
                variant="outline" className="bg-transparent border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white h-8 text-xs px-2"
                data-testid="mgr-new-save"><ArrowCounterClockwise size={14} className="mr-1" />New Save</Button>
            )}
            {/* "Sim Week" removed — user full-sims games one-by-one via competition tabs */}
            <Button data-testid="mgr-sim-all" onClick={simAllRemaining} disabled={seasonDone} variant="outline" className="bg-transparent border-[#2C2C35] text-white hover:bg-white hover:text-black h-8 text-xs px-2"><FastForward size={14} className="mr-1" />Sim All</Button>
            {seasonDone && <Button data-testid="advance-season-btn" onClick={advanceSeason} disabled={!allCupsDone} className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black font-bold h-8 text-xs px-2"><ArrowUp size={14} className="mr-1" />Next Season</Button>}
          </div>
        </div>

        {/* TABS */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#121216] border border-[#2C2C35] w-full grid mb-4" style={{ gridTemplateColumns: 'repeat(13, minmax(0, 1fr))' }}>
            {[
              { v:'standings', l:'League', i:Trophy, c:'#FF3B30' },
              { v:'fixtures', l:'Fixtures', i:CalendarDots, c:'#007AFF' },
              { v:'lineup', l:'Lineup', i:Strategy, c:'#32D74B' },
              { v:'transfers', l:'Transfers', i:ShoppingCart, c:'#007AFF' },
              { v:'scout', l:'Scout', i:Eye, c:'#FFD700' },
              { v:'cup', l:'Cup', i:GameController, c:'#FF6B00' },
              { v:'asiancup', l:'ASIAN', i:Globe, c:'#CCFF00' },
              { v:'cwc', l:'CWC', i:SoccerBall, c:'#32D74B' },
              { v:'worldcup', l:'WC', i:Globe, c:'#007AFF' },
              { v:'stats', l:'Stats', i:ChartBar, c:'#AF52DE' },
              { v:'academy', l:'Academy', i:GraduationCap, c:'#AF52DE' },
            ].map(t => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`mgr-tab-${t.v}`}
                className="data-[state=active]:text-white font-heading uppercase text-[9px] sm:text-[10px] py-1.5"
                style={{ '--active-bg': t.c }}
              >
                <t.i size={12} className="mr-0.5 hidden sm:inline" />{t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ===== STANDINGS ===== */}
          <TabsContent value="standings">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2"><Standings teams={gameState.teams} /></div>
              <div className="space-y-4">
                <AwardRace teams={gameState.teams} title="Award Races" type="manager" />
              </div>
            </div>
          </TabsContent>

          {/* ===== FIXTURES ===== */}
          <TabsContent value="fixtures">
            {fixMatch && fixState !== 'idle' ? (
              <div className="space-y-4">
                {fixSimMode === 'full' && fixMatch.home && fixMatch.away && (
                  <MatchPitchGraphics homeTeam={fixMatch.home} awayTeam={fixMatch.away} events={fixEvents} currentMinute={fixMinute} isLive={fixState === 'live' && !fixPaused} homeScore={fixHomeScore} awayScore={fixAwayScore} />
                )}
                {fixSimMode === 'quick' && (
                  <div className="card-surface rounded-sm p-6 text-center">
                    <div className="flex items-center justify-center gap-6">
                      <span className="font-heading font-bold text-xl text-white">{fixMatch.home.name}</span>
                      <span className="score-display text-5xl text-white">{fixHomeScore} - {fixAwayScore}</span>
                      <span className="font-heading font-bold text-xl text-white">{fixMatch.away.name}</span>
                    </div>
                    {fixState === 'done' && <p className="text-[#A0A0A5] mt-2 text-sm">FULL TIME</p>}
                  </div>
                )}
                {/* Pause/Resume for full sim */}
                {fixSimMode === 'full' && fixState === 'live' && (
                  <Button onClick={toggleFixPause} data-testid="fix-pause-btn" className={`w-full ${fixPaused ? 'bg-[#32D74B] hover:bg-[#32D74B]/80 text-black' : 'bg-[#FFD60A] hover:bg-[#FFD60A]/80 text-black'} font-bold text-lg py-3`}>
                    {fixPaused ? <><Play size={24} className="mr-2" weight="fill" /> RESUME MATCH</> : <><Pause size={24} className="mr-2" weight="fill" /> PAUSE MATCH</>}
                  </Button>
                )}
                {fixPaused && fixState === 'live' && <p className="text-center text-[#FFD60A] text-xs animate-pulse">Paused — make changes then resume</p>}
                <MatchEvents events={fixEvents} currentMinute={fixMinute} isLive={fixState === 'live' && !fixPaused} />
                {fixState === 'done' && <Button onClick={() => { setFixMatch(null); setFixState('idle'); if (fixControlRef.current) fixControlRef.current.stop(); }} className="w-full bg-[#FF3B30] text-white">Back to Fixtures</Button>}
              </div>
            ) : fixMatch ? (
              <div className="card-surface rounded-sm p-6 text-center space-y-4">
                <div className="flex items-center justify-center gap-8">
                  <div><div className="w-14 h-14 rounded-sm bg-gradient-to-br from-[#FF3B30] to-[#FF5C53] flex items-center justify-center mx-auto"><span className="font-heading font-black text-white text-xl">{fixMatch.home.city.substring(0,2).toUpperCase()}</span></div><p className="font-heading font-bold text-white mt-2">{fixMatch.home.name}</p><p className="text-xs text-[#6E6E73]">OVR {getTeamOverall(fixMatch.home)}</p></div>
                  <span className="font-heading text-3xl text-[#6E6E73]">VS</span>
                  <div><div className="w-14 h-14 rounded-sm bg-gradient-to-br from-[#007AFF] to-[#0A84FF] flex items-center justify-center mx-auto"><span className="font-heading font-black text-white text-xl">{fixMatch.away.city.substring(0,2).toUpperCase()}</span></div><p className="font-heading font-bold text-white mt-2">{fixMatch.away.name}</p><p className="text-xs text-[#6E6E73]">OVR {getTeamOverall(fixMatch.away)}</p></div>
                </div>
                <div className="flex gap-3 justify-center">
                  <Button onClick={runFixQuick} className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white"><Lightning size={16} className="mr-1" weight="fill" />Quick Sim</Button>
                  <Button onClick={runFixFull} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white"><Play size={16} className="mr-1" weight="fill" />Full Sim</Button>
                  <Button onClick={() => setFixMatch(null)} variant="outline" className="border-[#2C2C35] text-white hover:bg-white hover:text-black">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Current week header + Next Game Week */}
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-white uppercase">Week {gameState.currentWeek > 38 ? 38 : gameState.currentWeek} Fixtures</h3>
                  <Button onClick={simWeek} disabled={seasonDone} className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black font-bold" size="sm" data-testid="next-gameweek-btn">
                    <FastForward size={14} className="mr-1" /> Next Game Week
                  </Button>
                </div>

                {/* Current week results (played) */}
                <div className="card-surface rounded-sm">
                  <ScrollArea className="h-[220px]">
                    <div className="p-3 space-y-2">
                      {gameState.fixtures?.filter(f => f.week === (gameState.currentWeek > 38 ? 38 : gameState.currentWeek) - (gameState.currentWeek > 1 ? 1 : 0)).length === 0 && gameState.currentWeek <= 1 ? null :
                        gameState.fixtures?.filter(f => f.played).sort((a,b) => b.week - a.week).slice(0, 10).map(f => {
                          const h = gameState.teams.find(t => t.id === f.homeTeamId);
                          const a = gameState.teams.find(t => t.id === f.awayTeamId);
                          if (!h || !a) return null;
                          const isMine = h.id === selectedTeamId || a.id === selectedTeamId;
                          return (
                            <div key={f.id} className={`flex items-center gap-3 p-2 rounded-sm ${isMine ? 'bg-[#FFD700]/10 border border-[#FFD700]/20' : 'bg-[#0A0A0C]'}`}>
                              <span className="font-mono text-[10px] text-[#6E6E73] w-7">W{f.week}</span>
                              <span className="text-sm text-white flex-1 text-right truncate">{h.name}</span>
                              <span className="font-mono font-bold text-white text-sm px-2">{f.homeScore} - {f.awayScore}</span>
                              <span className="text-sm text-white flex-1 truncate">{a.name}</span>
                            </div>
                          );
                        })
                      }
                    </div>
                  </ScrollArea>
                  {gameState.fixtures?.filter(f => f.played).length === 0 && (
                    <p className="text-[#6E6E73] text-xs text-center py-4">No results yet. Click a fixture below to sim or use "Next Game Week".</p>
                  )}
                </div>

                {/* Upcoming fixtures to play — mixed across ALL competitions */}
                <div className="card-surface rounded-sm">
                  <div className="border-b border-[#2C2C35] px-4 py-2 flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase text-[#A0A0A5] tracking-wider">Upcoming — {myTeam?.name || 'All Competitions'}</h4>
                    <span className="text-[10px] text-[#6E6E73]">League · Cup · Asian Cup · CWC · WC</span>
                  </div>
                  <ScrollArea className="h-[300px]">
                    <div className="p-3 space-y-2">
                      {selectedTeamId && getMixedUpcomingForTeam(selectedTeamId, gameState).slice(0, 25).map((f, i) => {
                        const h = gameState.teams.find(t => t.id === f.homeTeamId) || (gameState.championsLeagueTeams || []).find(t => t.id === f.homeTeamId) || (gameState.cwcTeams || []).find(t => t.id === f.homeTeamId);
                        const a = gameState.teams.find(t => t.id === f.awayTeamId) || (gameState.championsLeagueTeams || []).find(t => t.id === f.awayTeamId) || (gameState.cwcTeams || []).find(t => t.id === f.awayTeamId);
                        if (!h || !a) return null;
                        const isLeague = f._comp === 'league';
                        return (
                          <button key={f.id + '_' + i} onClick={() => isLeague ? startFixMatch(f) : null}
                            className={`w-full flex items-center gap-3 p-2.5 rounded-sm border border-[#2C2C35] hover:border-[#FF3B30] transition-all text-left bg-[#121216] ${!isLeague ? 'cursor-default opacity-90' : ''}`}
                            data-testid={`rtg-upcoming-${i}`}>
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${f._compColor}22`, color: f._compColor, minWidth: '56px', textAlign: 'center' }}>{f._compLabel}</span>
                            <span className="text-sm text-white flex-1 text-right truncate">{h.name}</span>
                            <span className="text-xs text-[#6E6E73] px-1">vs</span>
                            <span className="text-sm text-white flex-1 truncate">{a.name}</span>
                            {isLeague ? <Play size={12} className="text-[#FF3B30]" weight="fill" /> : <span className="text-[10px] text-[#6E6E73]">→ tab</span>}
                          </button>
                        );
                      })}
                      {selectedTeamId && getMixedUpcomingForTeam(selectedTeamId, gameState).length === 0 && (
                        <p className="text-[#6E6E73] text-xs text-center py-4">All fixtures played!</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ===== LINEUP ===== */}
          <TabsContent value="lineup">
            {myTeam && (
              <div className="card-surface rounded-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-white uppercase flex items-center gap-2"><Strategy size={18} className="text-[#32D74B]" weight="fill" /> My Lineup</h3>
                  <div className="flex gap-2">
                    <Select value={myTeam.formation} onValueChange={(v) => {
                      onUpdateGameState({ ...gameState, teams: gameState.teams.map(t => {
                        if (t.id !== myTeam.id) return t;
                        const withForm = { ...t, formation: v };
                        const { lineup, bench } = buildLineupForFormation(withForm);
                        return { ...withForm, lineup, bench };
                      }) });
                    }}>
                      <SelectTrigger className="w-[110px] bg-[#0A0A0C] border-[#2C2C35] text-white text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121216] border-[#2C2C35]">{FORMATIONS.map(f => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" onClick={() => setShowSubModal(true)} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white text-xs"><ArrowsLeftRight size={14} className="mr-1" />Swap</Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <TacticalPitch team={myTeam} />
                  <div>
                    <h4 className="text-xs font-bold uppercase text-[#32D74B] mb-1">Starting XI</h4>
                    <ScrollArea className="h-[180px]">
                      {myTeam.lineup.map(num => { const p = myTeam.players.find(pl => pl.number === num); if (!p) return null; return (
                        <div key={p.id} className="flex items-center gap-2 px-2 py-1 hover:bg-[#1A1A1F] rounded-sm">
                          <span className="font-mono text-xs text-white w-5">{p.number}</span><span className={`position-badge ${POS_CLR(p.position)}`}>{p.position}</span>
                          <span className="text-sm text-white flex-1 truncate">{p.flag} {p.name}</span><span className="font-mono text-xs text-white">{p.overall}</span>
                          {p.injuredWeeks > 0 && <span className="text-[9px] text-[#FF6B00]">({p.injuredWeeks}w)</span>}
                        </div>); })}
                    </ScrollArea>
                    <h4 className="text-xs font-bold uppercase text-[#A0A0A5] mb-1 mt-2">Bench</h4>
                    <ScrollArea className="h-[120px]">
                      {myTeam.bench.map(num => { const p = myTeam.players.find(pl => pl.number === num); if (!p) return null; return (
                        <div key={p.id} className="flex items-center gap-2 px-2 py-1 hover:bg-[#1A1A1F] rounded-sm opacity-60">
                          <span className="font-mono text-xs text-white w-5">{p.number}</span><span className={`position-badge ${POS_CLR(p.position)}`}>{p.position}</span>
                          <span className="text-sm text-white flex-1 truncate">{p.flag} {p.name}</span><span className="font-mono text-xs text-white">{p.overall}</span>
                        </div>); })}
                    </ScrollArea>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ===== TRANSFERS ===== */}
          <TabsContent value="transfers">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card-surface rounded-sm">
                <div className="border-b border-[#2C2C35] px-4 py-3 flex justify-between"><h3 className="font-heading font-bold text-white uppercase flex items-center gap-2"><ShoppingCart size={16} />Market</h3><span className="text-xs text-[#32D74B] font-mono">{formatValue(budget)}</span></div>
                <ScrollArea className="h-[400px]"><div className="p-2 space-y-1">{marketPlayers.map(p => { const c = calculatePlayerValue(p); return (
                  <div key={p.id} className="flex items-center gap-2 p-2 hover:bg-[#1A1A1F] rounded-sm">
                    <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{p.flag} {p.name}</p><p className="text-[10px] text-[#6E6E73]">{p.position} | {p.age}y | OVR {p.overall} | POT {p.potential}</p></div>
                    <Button size="sm" onClick={() => buyPlayer(p)} disabled={c > budget} className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black text-xs"><CurrencyDollar size={12} />{formatValue(c)}</Button>
                  </div>); })}</div></ScrollArea>
              </div>
              <div className="space-y-4">
                {myTeam && <div className="card-surface rounded-sm"><div className="border-b border-[#2C2C35] px-4 py-3"><h3 className="font-heading font-bold text-white uppercase">Sell</h3></div>
                  <ScrollArea className="h-[200px]"><div className="p-2 space-y-1">{myTeam.players.map(p => (
                    <div key={p.id} className="flex items-center gap-2 p-1.5 hover:bg-[#1A1A1F] rounded-sm">
                      <div className="flex-1 min-w-0"><p className="text-xs text-white truncate">#{p.number} {p.flag} {p.name}</p><p className="text-[10px] text-[#6E6E73]">{p.position} | OVR {p.overall}</p></div>
                      <Button size="sm" onClick={() => sellPlayer(p)} disabled={myTeam.players.length <= 15} variant="outline" className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white text-[10px] h-6 px-2">Sell {formatValue(calculatePlayerValue(p))}</Button>
                    </div>))}</div></ScrollArea></div>}
                <div className="card-surface rounded-sm"><div className="border-b border-[#2C2C35] px-4 py-3"><h3 className="font-heading font-bold text-white uppercase">Offers ({incomingOffers.filter(o => o.status === 'pending').length})</h3></div>
                  <ScrollArea className="h-[180px]"><div className="p-2 space-y-2">
                    {incomingOffers.filter(o => o.status === 'pending').length === 0 && <p className="text-[#6E6E73] text-xs p-4 text-center">No pending offers</p>}
                    {incomingOffers.filter(o => o.status === 'pending').map(o => (
                      <div key={o.id} className="card-elevated rounded-sm p-2"><p className="text-xs text-white">{o.fromTeam.name} <span className="text-[#6E6E73]">({o.fromTeam.league})</span></p>
                        <p className="text-xs text-[#A0A0A5]">{o.player.name} ({o.player.position}) - <span className="text-[#32D74B] font-mono">{formatValue(o.amount)}</span></p>
                        <div className="flex gap-1 mt-1"><Button size="sm" onClick={() => acceptOffer(o)} className="bg-[#32D74B] text-black flex-1 h-6 text-[10px]"><Check size={10} />Accept</Button><Button size="sm" onClick={() => rejectOffer(o)} variant="outline" className="border-[#FF3B30] text-[#FF3B30] flex-1 h-6 text-[10px]"><X size={10} />Reject</Button></div>
                      </div>))}
                  </div></ScrollArea></div>

                {/* AI TRANSFER WINDOW LOG */}
                <div className="card-surface rounded-sm">
                  <div className="border-b border-[#2C2C35] px-4 py-3"><h3 className="font-heading font-bold text-white uppercase flex items-center gap-2"><ArrowsLeftRight size={14} />League Transfers{(gameState.aiTransferLog?.length || 0) > 0 && <span className="text-xs text-[#A0A0A5] font-normal">({gameState.aiTransferLog.length} moves this window)</span>}</h3></div>
                  <ScrollArea className="h-[200px]"><div className="p-2 space-y-1" data-testid="mgr-ai-transfer-log">
                    {!gameState.aiTransferLog?.length && <p className="text-[#6E6E73] text-xs p-4 text-center">Advance the season to trigger the AI transfer window</p>}
                    {(gameState.aiTransferLog || []).slice(0, 50).map((t, i) => (
                      <div key={i} className="border-l-2 pl-2 py-0.5" style={{ borderColor: t.type === 'big-move' ? '#FFD700' : t.type === 'retire' ? '#FF6B00' : '#2C2C35' }}>
                        {t.type === 'big-move' && <p className="text-[10px] text-white">⭐ <span className="text-[#FFD700]">{t.inPlayer}</span> joined <span className="font-bold">{t.to}</span> from {t.from}</p>}
                        {t.type === 'swap' && <p className="text-[10px] text-[#A0A0A5]">🔁 {t.outPlayer} ⇄ {t.inPlayer} ({t.from} ↔ {t.to})</p>}
                        {t.type === 'retire' && <p className="text-[10px] text-[#FF6B00]">🏁 {t.player} retired from {t.from}, replaced by {t.replacement}</p>}
                      </div>
                    ))}
                  </div></ScrollArea>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ===== SCOUT ===== */}
          <TabsContent value="scout">
            <div className="card-surface rounded-sm">
              <div className="border-b border-[#2C2C35] px-4 py-3 flex justify-between">
                <h3 className="font-heading font-bold text-white uppercase flex items-center gap-2"><UsersIcon size={16} />Scout</h3>
                <Select value={scoutTeamId || ''} onValueChange={setScoutTeamId}><SelectTrigger className="w-[200px] bg-[#0A0A0C] border-[#2C2C35] text-white text-xs"><SelectValue placeholder="Pick team" /></SelectTrigger>
                  <SelectContent className="bg-[#121216] border-[#2C2C35] max-h-72">{gameState.teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({getTeamOverall(t)})</SelectItem>)}</SelectContent></Select>
              </div>
              {scoutTeamId ? (() => { const st = gameState.teams.find(t => t.id === scoutTeamId); if (!st) return null; return (
                <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <TacticalPitch team={st} />
                  <ScrollArea className="h-[380px] card-elevated rounded-sm"><div className="p-1">
                    {[...st.players].sort((a,b) => b.overall - a.overall).map(p => (
                      <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#1A1A1F] rounded-sm">
                        <span className="font-mono text-xs text-[#6E6E73] w-5">#{p.number}</span><span className={`position-badge ${POS_CLR(p.position)}`}>{p.position}</span>
                        <span className="text-xs">{p.flag}</span><span className="text-sm text-white flex-1 truncate">{p.name}</span>
                        <span className="text-[10px] text-[#6E6E73]">{p.age}y</span><span className="font-mono text-sm text-white w-6 text-right">{p.overall}</span>
                        <span className="text-[8px] text-[#6E6E73]">/</span><span className={`font-mono text-xs w-6 ${p.potential >= 85 ? 'text-[#007AFF]' : 'text-[#A0A0A5]'}`}>{p.potential}</span>
                      </div>))}
                  </div></ScrollArea>
                </div>); })() : <div className="p-8 text-center text-[#6E6E73]">Select a team to scout</div>}
            </div>
          </TabsContent>

          {/* ===== CUP ===== */}
          <TabsContent value="cup">
            <div className="-mx-1"><CupPage gameState={gameState} onUpdateGameState={onUpdateGameState} modeLabel="RTG" /></div>
          </TabsContent>

          {/* ===== ASIAN CUP ===== */}
          <TabsContent value="asiancup">
            <div className="-mx-1"><AsianCupPage gameState={gameState} onUpdateGameState={onUpdateGameState} /></div>
          </TabsContent>


          {/* ===== CWC ===== */}
          <TabsContent value="cwc">
            <div className="-mx-1"><ClubWorldCupPage gameState={gameState} onUpdateGameState={onUpdateGameState} /></div>
          </TabsContent>

          {/* ===== WORLD CUP ===== */}
          <TabsContent value="worldcup">
            <div className="-mx-1"><WorldCupPage gameState={gameState} onUpdateGameState={onUpdateGameState} /></div>
          </TabsContent>

          {/* ===== STATS ===== */}
          <TabsContent value="stats">
            <div className="space-y-4">
              <div className="card-surface rounded-sm p-3">
                <h3 className="font-heading font-bold text-white text-sm uppercase mb-2">Awards by Competition</h3>
                <Tabs defaultValue="league">
                  <TabsList className="bg-[#121216] border border-[#2C2C35] w-full grid grid-cols-5 mb-3">
                    <TabsTrigger value="league" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase text-[10px]">League</TabsTrigger>
                    <TabsTrigger value="cup" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black font-heading uppercase text-[10px]">Cup</TabsTrigger>
                    <TabsTrigger value="championsLeague" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black font-heading uppercase text-[10px]">Asian Cup</TabsTrigger>
                    <TabsTrigger value="cwc" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white font-heading uppercase text-[10px]">CWC</TabsTrigger>
                    <TabsTrigger value="worldCup" className="data-[state=active]:bg-[#AF52DE] data-[state=active]:text-white font-heading uppercase text-[10px]">WC</TabsTrigger>
                  </TabsList>
                  <TabsContent value="league"><CompetitionAwards teams={gameState.teams || []} competition="league" topN={25} /></TabsContent>
                  <TabsContent value="cup"><CompetitionAwards teams={gameState.teams || []} competition="cup" topN={25} /></TabsContent>
                  <TabsContent value="championsLeague"><CompetitionAwards teams={[...(gameState.teams || []), ...(gameState.championsLeagueTeams || [])]} competition="championsLeague" topN={25} /></TabsContent>
                  <TabsContent value="cwc"><CompetitionAwards teams={[...(gameState.teams || []), ...(gameState.cwcTeams || [])]} competition="cwc" topN={25} /></TabsContent>
                  <TabsContent value="worldCup"><CompetitionAwards teams={gameState.wcTeams || []} competition="worldCup" topN={25} /></TabsContent>
                </Tabs>
              </div>
            </div>
          </TabsContent>

          {/* ===== ACADEMY ===== */}
          <TabsContent value="academy">
            {myTeam && <YouthAcademy team={myTeam} season={gameState.season || 1} onPromotePlayer={(player) => {
              if (!myTeam || myTeam.players.length >= 25) return;
              const num = Math.max(...myTeam.players.map(p => p.number)) + 1;
              const np = { ...player, number: num };
              onUpdateGameState({ ...gameState, teams: gameState.teams.map(t => t.id === selectedTeamId ? { ...t, players: [...t.players, np], bench: [...t.bench, num] } : t) });
            }} />}
          </TabsContent>
        </Tabs>
      </div>

      {myTeam && <SubstitutionModal isOpen={showSubModal} onClose={() => setShowSubModal(false)} team={myTeam} onSubstitute={(outNum, inNum) => {
        const nl = myTeam.lineup.map(n => n === outNum ? inNum : n);
        const nb = myTeam.bench.map(n => n === inNum ? outNum : n);
        onUpdateGameState({ ...gameState, teams: gameState.teams.map(t => t.id === myTeam.id ? { ...t, lineup: nl, bench: nb } : t) });
      }} subsRemaining={99} />}
    </div>
  );
};

// Reusable top-stats board (scorers + assists per competition)
const TopStatsBoard = ({ teams = [] }) => {
  const [comp, setComp] = React.useState('total');
  const all = React.useMemo(() => (teams || []).flatMap(t => (t.players || []).map(p => ({ ...p, teamName: t.name }))), [teams]);
  const scorers = React.useMemo(() => [...all].sort((a, b) => (getPlayerStat(b, 'goals', comp) || 0) - (getPlayerStat(a, 'goals', comp) || 0)).slice(0, 10), [all, comp]);
  const assists = React.useMemo(() => [...all].sort((a, b) => (getPlayerStat(b, 'assists', comp) || 0) - (getPlayerStat(a, 'assists', comp) || 0)).slice(0, 10), [all, comp]);
  const comps = [{ key: 'total', label: 'All' }, ...COMPETITIONS.map(c => ({ key: c.key, label: c.label }))];
  return (
    <div className="space-y-3">
      <div className="flex gap-1 flex-wrap">
        {comps.map(c => (
          <button key={c.key} onClick={() => setComp(c.key)}
            data-testid={`mgr-stat-filter-${c.key}`}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${comp === c.key ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-transparent text-[#A0A0A5] border-[#2C2C35]'}`}>
            {c.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-surface rounded-sm p-4">
          <h4 className="font-heading font-bold text-white text-sm uppercase mb-2">Top Scorers — {comps.find(c => c.key === comp)?.label}</h4>
          {scorers.map((p, i) => (
            <div key={p.id || i} className="flex items-center justify-between py-1.5 border-b border-[#2C2C35]/50 text-sm">
              <span className="text-white truncate">{i+1}. {p.name} <span className="text-[#6E6E73] text-xs">— {p.teamName}</span></span>
              <span className="font-mono text-[#FF3B30] font-bold">{getPlayerStat(p, 'goals', comp) || 0}</span>
            </div>
          ))}
        </div>
        <div className="card-surface rounded-sm p-4">
          <h4 className="font-heading font-bold text-white text-sm uppercase mb-2">Top Assists — {comps.find(c => c.key === comp)?.label}</h4>
          {assists.map((p, i) => (
            <div key={p.id || i} className="flex items-center justify-between py-1.5 border-b border-[#2C2C35]/50 text-sm">
              <span className="text-white truncate">{i+1}. {p.name} <span className="text-[#6E6E73] text-xs">— {p.teamName}</span></span>
              <span className="font-mono text-[#32D74B] font-bold">{getPlayerStat(p, 'assists', comp) || 0}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
